/**
 * Deployment configuration.
 *
 * Everything here comes from the CDK stack outputs. `scripts/configure-web.sh`
 * reads them out of CloudFormation and writes `.env.local`, so these values are
 * never hand-copied.
 *
 * Missing values are collected rather than thrown. A throw here happens while the
 * module graph is still evaluating, which no `try` can catch and which shows up
 * as a blank white page — the least debuggable failure a deploy can have. The
 * app checks `configProblems` at startup and says what is missing instead.
 *
 * Note what is *not* here: the `X-Aloha-Key` shared secret. The iOS app can hold
 * that because a phone binary is not readable; a web bundle is. The browser
 * authenticates as a real Cognito user instead.
 */

export const configProblems: string[] = [];

function required(name: string, value: string | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    configProblems.push(name);
    return '';
  }
  return trimmed;
}

const trailingSlash = /\/$/;

export const config = {
  apiEndpoint: required('VITE_API_ENDPOINT', import.meta.env.VITE_API_ENDPOINT).replace(
    trailingSlash,
    ''
  ),
  /** Optional: without it the app works, but only refreshes when asked. */
  webSocketEndpoint:
    import.meta.env.VITE_WEBSOCKET_ENDPOINT?.trim().replace(trailingSlash, '') ?? '',
  userPoolId: required('VITE_USER_POOL_ID', import.meta.env.VITE_USER_POOL_ID),
  userPoolClientId: required('VITE_USER_POOL_CLIENT_ID', import.meta.env.VITE_USER_POOL_CLIENT_ID),
} as const;

export const isConfigured = configProblems.length === 0;

export const realtimeEnabled = config.webSocketEndpoint.length > 0;
